import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Trash, MagnifyingGlass, ArrowsClockwise, Lightning,
  Hash, Scales, CurrencyInr, CaretDown, ForkKnife, FloppyDisk,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import { fmtINR, calcLine, api } from "../lib/kostra";
import { toast } from "sonner";

const newRow = () => ({
  id: crypto.randomUUID(),
  name: "",
  quantity: 0,
  unit: "g",
  rate_per_kg: 0,
  manual_override: false,
});

export default function IngredientsTable({
  title,
  subtitle,
  testIdPrefix,
  rows,
  onChange,
  benchmarks,
  benchmarkCategory,
  total,
  accent = "#0A3B24",
  onBenchmarksRefresh,
}) {
  const [search, setSearch] = useState("");
  const [openPickerId, setOpenPickerId] = useState(null);
  const [addingForRowId, setAddingForRowId] = useState(null);
  const [newRate, setNewRate] = useState("");
  const [savingNew, setSavingNew] = useState(false);

  const filtered = useMemo(() => {
    const list = (benchmarks || []).filter((b) => b.category === benchmarkCategory);
    if (!search) return list.slice(0, 80);
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const q = norm(search);
    const tokens = q.split(/\s+/).filter(Boolean);
    return list
      .filter((b) => {
        const n = norm(b.name);
        return tokens.every((t) => n.includes(t));
      })
      .slice(0, 80);
  }, [benchmarks, benchmarkCategory, search]);

  const updateRow = (id, patch) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => onChange(rows.filter((r) => r.id !== id));
  const addRow = () => onChange([...(rows || []), newRow()]);

  const refreshFromBenchmark = (id) => {
    const row = rows.find((r) => r.id === id);
    if (!row || !row.name) return;
    const match = (benchmarks || []).find(
      (b) => b.name.toLowerCase() === (row.name || "").toLowerCase()
    );
    if (match) updateRow(id, { rate_per_kg: match.rate_per_kg, manual_override: false });
  };

  const saveCustomIngredient = async (rowId) => {
    const name = (search || "").trim();
    const rate = parseFloat(newRate);
    if (!name) {
      toast.error("Type an ingredient name first");
      return;
    }
    if (!Number.isFinite(rate) || rate < 0) {
      toast.error("Enter a valid rate per kg (₹)");
      return;
    }
    setSavingNew(true);
    try {
      const { data } = await api.post("/ingredients/custom", {
        name,
        category: benchmarkCategory,
        rate_per_kg: rate,
      });
      toast.success(`Added "${data.name}" to database`);
      // populate this row
      updateRow(rowId, { name: data.name, rate_per_kg: data.rate_per_kg, manual_override: false });
      // reset add form
      setAddingForRowId(null);
      setNewRate("");
      setSearch("");
      setOpenPickerId(null);
      // refresh benchmarks in parent
      if (typeof onBenchmarksRefresh === "function") onBenchmarksRefresh();
    } catch (e) {
      toast.error("Could not save ingredient");
    } finally { setSavingNew(false); }
  };

  const inputCls =
    "h-11 w-full bg-white border-2 border-[#E5E7EB] rounded-md px-3 text-[15px] font-mono-num text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#0A3B24] focus:ring-2 focus:ring-[#0A3B24]/15 hover:border-[#0A3B24]/40 transition";

  return (
    <div className="kostra-card p-5 md:p-6" data-testid={`${testIdPrefix}-card`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 rounded-md flex items-center justify-center"
            style={{ background: `${accent}12`, color: accent }}
          >
            <ForkKnife size={20} weight="duotone" />
          </div>
          <div>
            <h3 className="kostra-section-title text-xl">{title}</h3>
            {subtitle && <p className="text-xs text-[#4B5563] mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div
          className="flex items-baseline gap-2 px-4 py-2 rounded-md border"
          style={{ background: `${accent}08`, borderColor: `${accent}33` }}
        >
          <span className="kostra-data-label">Subtotal</span>
          <span
            className="font-mono-num text-2xl font-semibold tracking-tight"
            style={{ color: accent }}
            data-testid={`${testIdPrefix}-total`}
          >
            {fmtINR(total)}
          </span>
        </div>
      </div>

      {/* Empty state */}
      {(rows || []).length === 0 && (
        <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center">
          <div className="text-sm text-[#4B5563]">No ingredients yet.</div>
          <div className="text-xs text-[#4B5563] mt-1">Click <strong>Add Ingredient</strong> to start building your recipe.</div>
        </div>
      )}

      {/* Rows */}
      <div className="space-y-3">
        <AnimatePresence initial={false}>
          {(rows || []).map((row, idx) => {
            const cost = calcLine(row.quantity, row.unit, row.rate_per_kg);
            const matchInDb = (benchmarks || []).some(
              (b) => b.name.toLowerCase() === (row.name || "").toLowerCase()
            );
            const isOpen = openPickerId === row.id;
            return (
              <motion.div
                key={row.id}
                layout
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="group rounded-lg border border-[#E5E7EB] bg-white hover:border-[#0A3B24]/30 hover:shadow-sm transition"
              >
                <div className="grid grid-cols-12 gap-3 p-3 md:p-4 items-end">
                  {/* Index badge */}
                  <div className="col-span-12 md:col-span-1 flex md:block items-center md:items-start justify-between">
                    <div
                      className="h-9 w-9 rounded-md font-mono-num text-sm font-bold inline-flex items-center justify-center"
                      style={{ background: `${accent}12`, color: accent }}
                    >
                      {idx + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      data-testid={`${testIdPrefix}-remove-${idx}`}
                      className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] transition"
                      aria-label="Remove ingredient"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>

                  {/* Ingredient picker */}
                  <div className="col-span-12 md:col-span-4">
                    <Label className="kostra-data-label flex items-center gap-1 mb-1.5">
                      <Hash size={11} weight="bold" /> Ingredient
                    </Label>
                    <Popover open={isOpen} onOpenChange={(v) => setOpenPickerId(v ? row.id : null)}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid={`${testIdPrefix}-name-${idx}`}
                          className={`${inputCls} flex items-center justify-between text-left ${
                            row.name ? "text-[#111827] font-semibold font-sans" : "text-[#9CA3AF] font-normal font-sans"
                          }`}
                        >
                          <span className="truncate flex items-center gap-2">
                            {row.name || "Search or pick ingredient..."}
                            {row.name && matchInDb && (
                              <span className="gold-pill text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full">
                                in DB
                              </span>
                            )}
                          </span>
                          <CaretDown size={14} className="text-[#4B5563] shrink-0 ml-2" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[340px]" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Search ingredients..."
                            value={search}
                            onValueChange={setSearch}
                          />
                          <CommandList className="max-h-72">
                            <CommandEmpty>
                              <div className="px-2 py-3 text-left">
                                <div className="text-sm font-medium text-[#111827]">
                                  No matches for "<span className="text-[#0A3B24]">{search || "..."}</span>"
                                </div>
                                <div className="text-[11px] text-[#4B5563] mt-1">
                                  Add this ingredient to your KOSTRA database — it'll be reusable across recipes.
                                </div>
                                {addingForRowId === row.id ? (
                                  <div className="mt-3 space-y-2" data-testid={`${testIdPrefix}-add-custom-form-${idx}`}>
                                    <Label className="kostra-data-label flex items-center gap-1">Rate per kg (₹)</Label>
                                    <div className="relative">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A3B24] font-bold text-sm">₹</span>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={newRate}
                                        onChange={(e) => setNewRate(e.target.value)}
                                        placeholder="e.g. 250"
                                        className="pl-7 h-10 font-mono-num border-2"
                                        autoFocus
                                        data-testid={`${testIdPrefix}-add-custom-rate-${idx}`}
                                      />
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                      <Button
                                        type="button"
                                        size="sm"
                                        disabled={savingNew}
                                        onClick={() => saveCustomIngredient(row.id)}
                                        className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold flex-1"
                                        data-testid={`${testIdPrefix}-add-custom-save-${idx}`}
                                      >
                                        <FloppyDisk size={14} weight="bold" className="mr-1" />
                                        {savingNew ? "Saving..." : "Save to DB"}
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => { setAddingForRowId(null); setNewRate(""); }}
                                        className="border-[#E5E7EB]"
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                    <div className="text-[10px] text-[#4B5563]">
                                      Saving as <strong>{search}</strong> · category{" "}
                                      <strong className="capitalize">{benchmarkCategory}</strong>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="mt-3 flex gap-2">
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={() => setAddingForRowId(row.id)}
                                      disabled={!search?.trim()}
                                      className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold flex-1"
                                      data-testid={`${testIdPrefix}-add-custom-${idx}`}
                                    >
                                      <Plus size={14} weight="bold" className="mr-1" /> Add to database
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        updateRow(row.id, { name: search });
                                        setOpenPickerId(null);
                                      }}
                                      disabled={!search?.trim()}
                                      className="border-[#0A3B24]/40 text-[#0A3B24]"
                                    >
                                      Use once
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </CommandEmpty>
                            <CommandGroup heading="Curated KOSTRA benchmarks">
                              {filtered.map((b) => (
                                <CommandItem
                                  key={b.name}
                                  value={b.name}
                                  onSelect={() => {
                                    updateRow(row.id, {
                                      name: b.name,
                                      rate_per_kg: row.manual_override ? row.rate_per_kg : b.rate_per_kg,
                                    });
                                    setOpenPickerId(null);
                                  }}
                                >
                                  <span className="flex-1 font-medium">{b.name}</span>
                                  <span className="font-mono-num text-xs text-[#4B5563]">
                                    ₹{b.rate_per_kg.toLocaleString("en-IN")}/kg
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Quantity */}
                  <div className="col-span-6 md:col-span-2">
                    <Label className="kostra-data-label flex items-center gap-1 mb-1.5">
                      <Scales size={11} weight="bold" /> Quantity
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.quantity}
                      onChange={(e) => updateRow(row.id, { quantity: parseFloat(e.target.value) || 0 })}
                      className={inputCls}
                      placeholder="0"
                      data-testid={`${testIdPrefix}-qty-${idx}`}
                    />
                  </div>

                  {/* Unit toggle */}
                  <div className="col-span-6 md:col-span-1">
                    <Label className="kostra-data-label mb-1.5 block">Unit</Label>
                    <div className="inline-flex rounded-md border-2 border-[#E5E7EB] bg-white p-0.5 h-11 w-full" role="group">
                      {["g", "kg"].map((u) => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => updateRow(row.id, { unit: u })}
                          className={`flex-1 text-xs font-bold uppercase rounded-[5px] transition ${
                            row.unit === u
                              ? "bg-[#0A3B24] text-white"
                              : "text-[#4B5563] hover:bg-[#0A3B24]/5"
                          }`}
                          data-testid={`${testIdPrefix}-unit-${idx}-${u}`}
                        >
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Rate per kg with prefix */}
                  <div className="col-span-12 md:col-span-2">
                    <Label className="kostra-data-label flex items-center gap-1 mb-1.5">
                      <CurrencyInr size={11} weight="bold" /> Rate / kg
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A3B24] font-bold text-sm pointer-events-none">₹</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.rate_per_kg}
                        onChange={(e) =>
                          updateRow(row.id, { rate_per_kg: parseFloat(e.target.value) || 0, manual_override: true })
                        }
                        className={`${inputCls} pl-7 pr-2`}
                        placeholder="0.00"
                        data-testid={`${testIdPrefix}-rate-${idx}`}
                      />
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-1">
                      <button
                        type="button"
                        onClick={() => refreshFromBenchmark(row.id)}
                        disabled={!matchInDb}
                        title={matchInDb ? "Reset rate to KOSTRA benchmark" : "Select a benchmarked ingredient first"}
                        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider transition ${
                          row.manual_override
                            ? "bg-[#FDF9EE] text-[#0A3B24] border border-[#D4AF37]/60"
                            : "bg-[#0A3B24]/5 text-[#0A3B24] border border-[#0A3B24]/20"
                        } ${matchInDb ? "hover:bg-[#0A3B24]/10 cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
                        data-testid={`${testIdPrefix}-source-${idx}`}
                      >
                        {row.manual_override ? (
                          <><Lightning size={10} weight="fill" /> Manual</>
                        ) : (
                          <><ArrowsClockwise size={10} weight="bold" /> Market</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Live cost pill */}
                  <div className="col-span-10 md:col-span-2">
                    <Label className="kostra-data-label mb-1.5 block">Line Cost</Label>
                    <motion.div
                      key={cost}
                      initial={{ scale: 0.96 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.2 }}
                      className="h-11 w-full rounded-md flex items-center justify-end px-3 font-mono-num text-lg font-bold tabular-nums"
                      style={{
                        background: cost > 0 ? `${accent}10` : "#F9FAFB",
                        color: cost > 0 ? accent : "#9CA3AF",
                        border: `1px solid ${cost > 0 ? `${accent}33` : "#E5E7EB"}`,
                      }}
                      data-testid={`${testIdPrefix}-cost-${idx}`}
                    >
                      {fmtINR(cost)}
                    </motion.div>
                  </div>

                  {/* Remove (desktop) */}
                  <div className="hidden md:flex col-span-12 md:col-span-12 lg:col-span-12 xl:col-span-12 md:!col-span-12 md:[grid-column:span_12] items-end justify-end -mt-2">
                    {/* placeholder — actual desktop trash is positioned absolutely below */}
                  </div>
                </div>

                {/* Desktop trash button (top-right) */}
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="hidden md:inline-flex absolute opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                  aria-hidden
                  tabIndex={-1}
                />
                <div className="hidden md:flex border-t border-[#F1F2F4] px-4 py-2 items-center justify-between bg-[#FAFBFB] rounded-b-lg">
                  <span className="text-[11px] text-[#4B5563]">
                    {row.name && row.quantity > 0 ? (
                      <>
                        Using <strong className="text-[#0A3B24]">{row.quantity}{row.unit}</strong> at <strong className="text-[#0A3B24]">{fmtINR(row.rate_per_kg)}/kg</strong>
                      </>
                    ) : (
                      <>Fill in the fields above to see the live cost.</>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    data-testid={`${testIdPrefix}-remove-desktop-${idx}`}
                    className="inline-flex items-center gap-1 text-[11px] text-[#4B5563] hover:text-[#DC2626] font-medium transition"
                    aria-label="Remove ingredient"
                  >
                    <Trash size={12} weight="bold" /> Remove
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Footer / add */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          onClick={addRow}
          data-testid={`${testIdPrefix}-add`}
          className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold h-11 px-5"
        >
          <Plus size={16} weight="bold" className="mr-2" /> Add Ingredient
        </Button>
        <span className="text-xs text-[#4B5563] inline-flex items-center gap-1.5">
          <MagnifyingGlass size={12} weight="bold" />
          Tap the ingredient field to search the curated price database
        </span>
      </div>
    </div>
  );
}
